import { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  RefreshCw, 
  Eye, 
  Upload, 
  X, 
  Filter, 
  ChevronDown,
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Main Employee CheckList Modal Component
export default function EmployeeCheckListModal({ 
  isOpen, 
  onClose, 
  title = "Employee Document Status",
  size = 'full',
  employeeData = [],
  onViewDocument,
  onUploadDocument,
  onRefresh
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const modalRef = useRef(null);

  // Mock data - same as original component
  const mockData = [
    {
      eid: '50015952',
      employee_name: 'Hemanth N D',
      Aadhar: {
        status: true,
        link: 'https://drive.google.com/file/d/1Pbp6TDJgP-X7ATEw1c2npAmZwZxP2XTQ/view',
      },
      Bank_First_Page: false,
      CV: {
        status: true,
        link: 'https://drive.google.com/file/d/11yibeHUquQs-8QJQWAaQ3Uw5YiFaN-30/view',
      },
      Certificate_by_the_employer: false,
      Education: false,
      Form_F: {
        status: true,
        link: 'https://drive.google.com/file/d/1QHMHmn8prqMLAv6xMhknR_9gzhm-cB8E/view',
      },
      Personal_Info: {
        status: true,
        link: 'https://drive.google.com/file/d/1exr7R-w1uHw1reqQPg36IzRFtUHurhVx/view',
      },
      Personal_Information_sheet: false,
      Previous_Office_Payslip: false,
      Previous_company_resignation_acceptance: false,
      Statement: {
        status: true,
        link: 'https://drive.google.com/file/d/1GUsfY1sNkwqBJ878PW65Ry01VXmszkiO/view',
      },
      certificate_by_the_employer: {
        status: true,
        link: 'https://drive.google.com/file/d/11PKpwm1h8Mpe1KbyQ_FmYBHwUCNos8le/view',
      },
      company_chain: false,
      personal_info_sheet: {
        status: false,
        link: 'https://drive.google.com/file/d/1q_oVRbO4Aj95kKR74u8OUszLCPoz8__4/view',
      },
    },
    {
      eid: '50015979',
      employee_name: 'Yusuf Abbas',
      Aadhar: {
        status: true,
        link: 'https://drive.google.com/file/d/1Pbp6TDJgP-X7ATEw1c2npAmZwZxP2XTQ/view',
      },
      Bank_First_Page: false,
      CV: {
        status: true,
        link: 'https://drive.google.com/file/d/11yibeHUquQs-8QJQWAaQ3Uw5YiFaN-30/view',
      },
      Certificate_by_the_employer: {
        status: true,
        link: 'https://drive.google.com/file/d/11PKpwm1h8Mpe1KbyQ_FmYBHwUCNos8le/view',
      },
      Education: {
        status: true,
        link: 'https://drive.google.com/file/d/1B5qotD3bHtBFeiatzxAkn_8Yd4I5uqWX/view',
      },
      Personal_Info: {
        status: true,
        link: 'https://drive.google.com/file/d/1exr7R-w1uHw1reqQPg36IzRFtUHurhVx/view',
      },
      Personal_Information_sheet: {
        status: true,
        link: 'https://drive.google.com/file/d/1cduUoaDK6c4kIYzFf8HOaCZasBweyHuZ/view',
      },
      Previous_Office_Payslip: {
        status: true,
        link: 'https://drive.google.com/file/d/1jrWm_PlFv5bVPihf4NMVy5z8kIbTE_kk/view',
      },
      Previous_company_resignation_acceptance: {
        status: true,
        link: 'https://drive.google.com/file/d/1b31teWhDmY9x4KAZKOQ64VPIrMonpYaH/view',
      },
      Statement: {
        status: true,
        link: 'https://drive.google.com/file/d/1GUsfY1sNkwqBJ878PW65Ry01VXmszkiO/view',
      },
      certificate_by_the_employer: false,
      company_chain: {
        status: true,
        link: 'https://drive.google.com/file/d/1BiZDdoutJykbJ19q5dgKAW1p8PiB8oQk/view',
      },
      personal_info_sheet: {
        status: true,
        link: 'https://www.fjsolicitors.co.uk/wp-content/uploads/2022/12/drafting-legal-documents-scaled.jpg',
      },
    }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchDocuments();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const dataToUse = employeeData.length > 0 ? employeeData : mockData;
      
      if (dataToUse.length > 0) {
        const types = Object.keys(dataToUse[0]).filter(key => key !== 'eid' && key !== 'employee_name');
        setDocumentTypes(types);
        setDocuments(dataToUse);
        
        const initialFilters = {};
        types.forEach(type => {
          initialFilters[type] = { hasDocument: false, noDocument: false };
        });
        setFilters(initialFilters);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (docType, filterType, checked) => {
    setFilters(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        [filterType]: checked
      }
    }));
  };

  const getFilteredDocuments = () => {
    let filtered = documents.filter(doc =>
      doc.eid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    Object.entries(filters).forEach(([docType, filter]) => {
      if (filter.hasDocument || filter.noDocument) {
        filtered = filtered.filter(doc => {
          const docValue = doc[docType];
          const hasDocument = typeof docValue === 'object' ? docValue.status : Boolean(docValue);
          
          if (filter.hasDocument && filter.noDocument) {
            return true;
          } else if (filter.hasDocument) {
            return hasDocument;
          } else if (filter.noDocument) {
            return !hasDocument;
          }
          return true;
        });
      }
    });

    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();
  const hasActiveFilters = Object.values(filters).some(filter => 
    filter.hasDocument || filter.noDocument
  );

  const clearAllFilters = () => {
    const clearedFilters = {};
    documentTypes.forEach(type => {
      clearedFilters[type] = { hasDocument: false, noDocument: false };
    });
    setFilters(clearedFilters);
  };

  const handleViewDocument = (url) => {
    if (onViewDocument) {
      onViewDocument(url);
    }
  };

  const handleUploadClick = (employeeId, documentType) => {
    if (onUploadDocument) {
      onUploadDocument(employeeId, documentType);
    }
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      await onRefresh();
    }
    await fetchDocuments();
  };

  // Calculate statistics
  const totalEmployees = documents.length;
  const totalDocuments = documents.reduce((acc, doc) => {
    return acc + documentTypes.reduce((docAcc, type) => {
      const docValue = doc[type];
      const hasDocument = typeof docValue === 'object' ? docValue.status : Boolean(docValue);
      return docAcc + (hasDocument ? 1 : 0);
    }, 0);
  }, 0);

  const getSizeClasses = () => {
    const sizes = {
      sm: 'max-w-2xl',
      md: 'max-w-4xl',
      lg: 'max-w-6xl',
      xl: 'max-w-7xl',
      full: 'max-w-[95vw]'
    };
    return sizes[size] || sizes.full;
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: -20
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      y: -20,
      transition: {
        duration: 0.2
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-auto">
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
          />
          
          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              ref={modalRef}
              className={`relative w-full ${getSizeClasses()} bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col`}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Enhanced Header */}
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <FileText size={24} className="text-white" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full">
                        <span className="absolute inset-0 w-full h-full bg-green-400 rounded-full animate-ping opacity-75"></span>
                      </span>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold flex items-center">
                        {title}
                        <Users className="ml-3 text-blue-200" size={20} />
                      </h2>
                      <div className="text-sm text-blue-100 flex items-center mt-1">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Document Management System
                      </div>
                    </div>
                  </div>
                  
                  {/* Statistics */}
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{totalEmployees}</div>
                      <div className="text-xs text-blue-200">Employees</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{totalDocuments}</div>
                      <div className="text-xs text-blue-200">Documents</div>
                    </div>
                    <motion.button
                      onClick={handleClose}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X size={20} className="text-white" />
                    </motion.button>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-10 -translate-x-10"></div>
              </div>

              {/* Controls Bar */}
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        placeholder="Search by Employee ID or Name..."
                        className="pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[300px]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    {hasActiveFilters && (
                      <motion.button
                        onClick={clearAllFilters}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Clear Filters
                      </motion.button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-600 bg-white px-3 py-2 rounded-md border">
                      {filteredDocuments.length} of {documents.length} employees
                    </div>
                    <motion.button 
                      className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
                      onClick={handleRefresh}
                      disabled={isLoading}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                    </motion.button>
                  </div>
                </div>
                
                {/* Active filters display */}
                {hasActiveFilters && (
                  <motion.div 
                    className="mt-3 pt-3 border-t"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-sm text-gray-600">Active filters:</span>
                      {Object.entries(filters).map(([docType, filter]) => {
                        const activeFilters = [];
                        if (filter.hasDocument) activeFilters.push('Has Document');
                        if (filter.noDocument) activeFilters.push('No Document');
                        
                        if (activeFilters.length > 0) {
                          return (
                            <motion.span
                              key={docType}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                            >
                              {docType.split('_').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                              ).join(' ')}: {activeFilters.join(', ')}
                              <button
                                onClick={() => {
                                  setFilters(prev => ({
                                    ...prev,
                                    [docType]: { hasDocument: false, noDocument: false }
                                  }));
                                }}
                                className="ml-1 hover:text-blue-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </motion.span>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Table Container */}
              <div className="flex-1 overflow-auto bg-white">
                <div className="min-w-full" style={{ minWidth: 'max-content' }}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="sticky left-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                          Employee ID
                        </th>
                        <th className="sticky left-[120px] z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[150px]">
                          Employee Name
                        </th>
                        {documentTypes.map((docType) => (
                          <th key={docType} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-center leading-tight">
                                {docType.split('_').map(word => 
                                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                ).join(' ')}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={documentTypes.length + 2} className="px-6 py-8 text-center text-sm text-gray-500">
                            <div className="flex items-center justify-center space-x-2">
                              <Clock className="animate-spin h-4 w-4" />
                              <span>Loading document status...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredDocuments.length > 0 ? (
                        filteredDocuments.map((doc, index) => (
                          <motion.tr 
                            key={doc.eid} 
                            className="hover:bg-gray-50 transition-colors"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                              {doc.eid}
                            </td>
                            <td className="sticky left-[120px] z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                              {doc.employee_name}
                            </td>
                            {documentTypes.map((docType) => {
                              const docValue = doc[docType];
                              const hasDocument = typeof docValue === 'object' ? docValue.status : Boolean(docValue);
                              const documentLink = typeof docValue === 'object' && docValue.status ? docValue.link : '';
                              
                              return (
                                <td key={`${doc.eid}-${docType}`} className="px-2 py-4 whitespace-nowrap text-center">
                                  {hasDocument ? (
                                    <motion.button
                                      onClick={() => documentLink && handleViewDocument(documentLink)}
                                      className="text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-full hover:bg-green-50"
                                      disabled={!documentLink}
                                      title="View document"
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                    >
                                      <Eye className="h-5 w-5 mx-auto" />
                                    </motion.button>
                                  ) : (
                                    <motion.button
                                      onClick={() => handleUploadClick(doc.eid, docType)}
                                      className="text-gray-400 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50"
                                      title="Upload document"
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                    >
                                      <Upload className="h-5 w-5 mx-auto" />
                                    </motion.button>
                                  )}
                                </td>
                              );
                            })}
                          </motion.tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={documentTypes.length + 2} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center space-y-3">
                              <AlertCircle className="h-12 w-12 text-gray-400" />
                              <div className="text-sm text-gray-500">
                                {hasActiveFilters ? 'No documents found matching your search and filter criteria.' : 'No documents found matching your search.'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 flex items-center space-x-4">
                    <span>Document Management System</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs">System Online</span>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <motion.button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Close
                    </motion.button>
                    <motion.button
                      className="px-4 py-2 text-sm bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white rounded-md hover:opacity-90 transition-opacity flex items-center space-x-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Download className="h-4 w-4" />
                      <span>Export Report</span>
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Example usage component
export function EmployeeCheckListModalDemo() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewDocumentUrl, setViewDocumentUrl] = useState('');
  const [uploadData, setUploadData] = useState({ employeeId: '', documentType: '' });

  const handleViewDocument = (url) => {
    setViewDocumentUrl(url);
    console.log('Opening document:', url);
    // You could open another modal here to view the document
  };

  const handleUploadDocument = (employeeId, documentType) => {
    setUploadData({ employeeId, documentType });
    console.log('Upload document for:', { employeeId, documentType });
    // You could open an upload modal here
  };

  const handleRefresh = async () => {
    console.log('Refreshing data...');
    // Implement your refresh logic here
  };

  return (
    <div className="p-8 min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          Employee CheckList Modal Demo
        </h1>
        
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
          <div className="text-center">
            <motion.button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white rounded-lg hover:opacity-90 transition-opacity text-lg font-medium"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Open Employee Document Modal
            </motion.button>
          </div>
        </div>

        <EmployeeCheckListModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Employee Document Status Dashboard"
          size="full"
          employeeData={[]} // Pass your data here, empty array will use mock data
          onViewDocument={handleViewDocument}
          onUploadDocument={handleUploadDocument}
          onRefresh={handleRefresh}
        />

        {/* Debug info */}
        {(viewDocumentUrl || uploadData.employeeId) && (
          <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
            <h3 className="font-medium mb-2">Debug Info:</h3>
            {viewDocumentUrl && (
              <div className="text-sm text-gray-600 mb-2">
                Last viewed document: {viewDocumentUrl}
              </div>
            )}
            {uploadData.employeeId && (
              <div className="text-sm text-gray-600">
                Last upload request: Employee {uploadData.employeeId}, Document Type: {uploadData.documentType}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}